import { ReactElement } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { PulsePollingSurveyDataJSON, AnswerSource } from 'src/redux/survey/survey.types';

interface PulsePollingChartProps {
  readonly data: PulsePollingSurveyDataJSON;
  readonly answerIndex: number;
  readonly answerFilter: AnswerSource;
  readonly isNormalized: boolean;
  readonly isLineChart: boolean;
}

const PulsePollingChart = ({ data, answerIndex, answerFilter, isNormalized, isLineChart }: PulsePollingChartProps): ReactElement => {
  const questionId = Object.keys(data.questions)[answerIndex];
  const question = data.questions[questionId];
  if (!question) {
    return <></>;
  }

  const colorPalette = ["#FF6B6B", "#FFD93D", "#6BCB77", "#4D96FF", "#9D4EDD", "#F38BA0", "#00C49A", "#FFA500"];
  const barHeight = 56;
  const labelFontWeight = 500;
  const labelFontSize = 14;
  const valueFontWeight = 400;

  const chartData = question.survey_runs.map((run) => {
    const runData: Record<string, number | string> = {
      date: new Date(run.survey_run_started_at).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        timeZone: 'UTC',
      }),
    };
    Object.values(run.answers).forEach((answer) => {
      const totalUsers = Object.values(run.answers).reduce(
        (sum, a) => {
          const answerData = a as any;
          switch (answerFilter) {
            case AnswerSource.AI:
              return sum + (answerData.ai_users || 0);
            case AnswerSource.Human:
              return sum + (answerData.human_users || 0);
            default:
              return sum + (answerData.total_users || 0);
          }
        },
        0
      );
      const answerData = answer as any;
      const userCount = answerFilter === AnswerSource.AI
        ? answerData.ai_users || 0
        : answerFilter === AnswerSource.Human
          ? answerData.human_users || 0
          : answerData.total_users || 0;
      runData[answer.title] = isNormalized
        ? ((userCount / totalUsers) * 100)
        : userCount;
    });
    return runData;
  }).reverse();

  // Get unique answer choices for the legend
  const answerChoices = Array.from(
    new Set(
      question.survey_runs.flatMap((run) =>
        Object.values(run.answers).map((answer) => answer.title)
      )
    )
  );

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, item: any) => sum + item.value, 0);
      const runIndex = chartData.findIndex((d) => d.date === label);
      const runNumber = runIndex >= 0 ? runIndex + 1 : '';
      return (
        <div
          className="bg-[#232323] p-3 rounded-xl shadow-lg border border-gray-700 z-[999]"
        >
          <p className="font-semibold mb-2 text-white text-base">
            {`Run ${runNumber}: ${label}`}
          </p>
          {payload.map((entry: any, index: number) => (
            <p
              key={index}
              className="text-white text-base"
              style={{ fontWeight: valueFontWeight }}
            >
              {entry.name}
              {': '}
              {isNormalized
                ? entry.value.toFixed(1) + '%'
                : Math.round(entry.value)}
              {' ('}
              {((entry.value / total) * 100).toFixed(1)}
              {'%)'}
            </p>
          ))}
          <p
            className="text-white text-base mt-2"
            style={{ fontWeight: valueFontWeight }}
          >
            {'Total: '}
            {Math.round(total)}
          </p>
        </div>
      );
    }
    return null;
  };

  const renderYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    return (
      <text
        x={x - 8}
        y={y + 2}
        fill="#fff"
        fontWeight={labelFontWeight}
        fontSize={labelFontSize}
        textAnchor="end"
        alignmentBaseline="middle"
        style={{ pointerEvents: 'none' }}
      >
        {payload.value}
      </text>
    );
  };

  const renderXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    return (
      <text
        x={x}
        y={y + 20}
        fill="#fff"
        fontWeight={labelFontWeight}
        fontSize={labelFontSize}
        textAnchor="middle"
        style={{ pointerEvents: 'none' }}
      >
        {payload.value}
      </text>
    );
  };

  const chartHeight = isLineChart
    ? 400
    : chartData.length * (barHeight + 8) + (answerChoices.length * 20);

  return (
    <div className="flex flex-col gap-4 bg-white/5 rounded-xl">
      <div
        className="h-auto flex flex-col gap-2"
        style={{ minHeight: `${chartHeight}px` }}
      >
        <ResponsiveContainer
          width="100%"
          height={chartHeight}
        >
          {isLineChart ? (
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis
                dataKey="date"
                tick={renderXAxisTick}
                axisLine={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1 }}
                tickLine={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1 }}
              />
              <YAxis
                tick={renderYAxisTick}
                axisLine={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1 }}
                tickLine={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1 }}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
              />
              <Legend
                wrapperStyle={{
                  fontWeight: 500,
                  fontSize: 16,
                  borderRadius: 16,
                  width: '95%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 40,
                  marginBottom: 0,
                  padding: '2px 0',
                }}
                iconType="circle"
                align="center"
                verticalAlign="bottom"
              />
              {answerChoices.map((answer, index) => (
                <Line
                  key={answer}
                  type="monotone"
                  dataKey={answer}
                  stroke={colorPalette[index]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              ))}
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.1)"
                vertical={false}
              />
            </LineChart>
          ) : (
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              barCategoryGap={8}
              barGap={2}
            >
              <XAxis
                type="number"
                hide
                tick={false}
                axisLine={false}
              />
              <YAxis
                dataKey="date"
                type="category"
                width={80}
                tick={renderYAxisTick}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              />
              <Legend
                wrapperStyle={{
                  fontWeight: 500,
                  fontSize: 16,
                  borderRadius: 16,
                  width: '95%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 40,
                  marginBottom: 0,
                  padding: '2px 0',
                }}
                iconType="circle"
                align="center"
                verticalAlign="bottom"
              />
              {answerChoices.map((answer, index) => (
                <Bar
                  key={answer}
                  dataKey={answer}
                  stackId="a"
                  fill={colorPalette[index]}
                  radius={[0, 0, 0, 0]}
                  barSize={barHeight}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PulsePollingChart;